import { Tabs } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Sticky, StickyContainer } from "react-sticky";
import { AlertBanner } from "../../components/AlertBanner";
import { useAuth } from "../../contexts/AuthContext";
import WithAuth from "../../hoc/withAuth";
import { useSeller } from "../../hooks/useSeller";
import { useSplit } from "../../hooks/useSlipt";
import { PadlockIconClosed } from "../../styles/icons/PadlockClosed";
import { Rewind } from "../../styles/icons/Rewind";
import { getInitials } from "../../utils";
import { AntecipationOnDemand } from "../AntecipationOnDemand";
import { TransactionalCosts } from "../TransactionalCosts";
import { Button } from "../TransactionalCosts/styles";
import { useTransactionalCosts } from "../TransactionalCosts/useTransactionalCosts";
import { TransactionalCostsV2 } from "../TransactionalCostsV2";
import { AntiFraudSection } from "./_AntiFraudSection";
import { BankingSection } from "./_BankingSection";
import { FinancialSection } from "./_FinancialSection";
import { InvoiceSection } from "./_InvoiceSection";
import { PaymentApprovalSection } from "./_PaymentApprovalSection";
import { SellerForm } from "./_SellerForm";
import { OwnersSection } from "./_OwnersSection";
import { Badge, Container, Flex, Header, Image, Text } from "./styles";

export const Seller = () => {
  const [undoChangesAlertState, setUndoChangesAlerState] =
    useState<boolean>(false);
  const [defaultAlertState, setDefaultAlertState] = useState<boolean>(false);
  const [isPageNotLoaded, setIsPageNotLoaded] = useState<boolean>(true);
  const location = useLocation();
  const [tabStatus, setTabStatus] = useState<string>("GENERAL");
  let { id } = useParams();
  const { TabPane } = Tabs;
  const { seller, getSellerData } = useSeller();
  const { getSliptInitialData, sellerSplitData } = useSplit();
  const { editionModeState, setEditionModeState } = useAuth();
  const navigate = useNavigate();
  const {
    currentSellerRates,
    defaultSellerRates,
    colorFinder,
    returnDefaultValue,
    hasReturnValue,
    setCurrentSellerRates,
    onSubmit,
    handleSubmit,
    register,
    handleValue,
    watch,
    errors,
    setValue,
    control,
    setValuesToDefault,
    undoChanges,
    confirmationModalState,
    setConfirmationModalState,
    allValuesAreDefault,
    sellerRatesHistory,
    handleGetSellerRatesHistory,
    allValuesAreEqualToCurrentSellerRates,
    allSellerRatesAreDefault,
    allBuyerRatesAreDefault,
    handleGetFullSellerRatesHistory,
    fullHistoryResponse,
    verifyAllValuesAreCorrect,
  } = useTransactionalCosts();

  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  const tabs = [
    { tab: "Geral", key: "GENERAL", status: "GENERAL" },
    { tab: "Financeiro", key: "FINANCIAL", status: "FINANCIAL" },
    { tab: "Nota Fiscal", key: "INVOICE", status: "INVOICE" },
    {
      tab: "Custos Transacionais",
      key: "TRANSACTIONAL_COSTS",
      status: "TRANSACTIONAL_COSTS",
    },
    {
      tab: "Custos Transacionais",
      key: "TRANSACTIONAL_COSTS v2",
      status: "TRANSACTIONAL_COSTS v2",
    },
    { tab: "Anti-fraude", key: "ANTI_FRAUD", status: "ANTI_FRAUD" },
    {
      tab: "Antecipação",
      key: "ANTECIPATION_ON_DEMAND",
      status: "ANTECIPATION_ON_DEMAND",
    },
    { tab: "Sócios", key: "OWNERS", status: "OWNERS" },
    ...(user?.email?.includes("@barte")
      ? [
          { tab: "Banking", key: "BANKING", status: "BANKING" },
          {
            tab: "Aprovação de pagamentos",
            key: "PAYMENT_APPROVAL",
            status: "PAYMENT_APPROVAL",
          },
        ]
      : []),
  ];

  const filteredTabs = tabs.filter((tab) => {
    if (tab.key === "TRANSACTIONAL_COSTS" && !seller?.isActiveForTax) {
      return true;
    }
    if (tab.key === "TRANSACTIONAL_COSTS" && seller?.isActiveForTax) {
      return false;
    }
    if (tab.key === "TRANSACTIONAL_COSTS v2" && !seller?.isActiveForTax) {
      return false;
    }
    return true;
  });

  const renderTabBar = (props, DefaultTabBar) => (
    <Sticky bottomOffset={80}>
      {({ style }) => (
        <DefaultTabBar
          {...props}
          className="site-custom-tab-bar"
          style={{ ...style, fontSize: "1px" }}
        />
      )}
    </Sticky>
  );

  async function getInitialData(id: string) {
    if (id === undefined) return;
    try {
      await getSellerData(id);
    } finally {
      setIsPageNotLoaded(false);
    }
  }

  function handleConcludeSettings() {
    if (!editionModeState) {
      setEditionModeState(true);
      return;
    } else if (editionModeState) {
      if (allValuesAreEqualToCurrentSellerRates) {
        setEditionModeState(false);
        verifyAllValuesAreCorrect(false);
        return;
      }
      const hasIncorrectValues = verifyAllValuesAreCorrect(true);
      if (hasIncorrectValues) return;
      return setConfirmationModalState(!confirmationModalState);
    }
  }

  useEffect(() => {
    getInitialData(id);
    getSliptInitialData(id);
    // eslint-disable-next-line
  }, [id, tabStatus]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("approvePayments")) {
      setTabStatus("PAYMENT_APPROVAL");
    }
  }, [location.search]);

  return (
    <>
      <Flex
        alignItems="center"
        padding="2rem 3rem 0"
        gap=".8rem"
        flexDir="column"
      >
        {seller?.isBlocked && (
          <AlertBanner
            iconTitle={<PadlockIconClosed />}
            title="Seller com o acesso bloqueado"
            description="Ao ter acesso bloqueado, não é possível fazer login nem emitir ou receber pagamentos.
                        O desbloqueio pode ser feito na seção de Login abaixo."
          />
        )}

        <Flex width="100%">
          {seller?.icon ? (
            <Image src={seller?.icon} />
          ) : (
            <Badge>
              <Text>
                {getInitials(seller?.companyName ? seller?.companyName : "")}
              </Text>
            </Badge>
          )}
          <Header>{seller?.companyName}</Header>
        </Flex>
      </Flex>
      <Container>
        <Flex
          className="sticky-button"
          position="sticky"
          top="0"
          gap=".8rem"
          zIndex="2"
          alignSelf="flex-end"
          width="100%"
        >
          <Flex position="absolute" top=".5rem" right="0">
            {tabStatus === "TRANSACTIONAL_COSTS" && editionModeState && (
              <Button
                disabled={allValuesAreDefault}
                bgColor="transparent"
                onClick={() => setDefaultAlertState(!defaultAlertState)}
              >
                <Flex gap=".5rem">
                  <Rewind color={allValuesAreDefault ? "#00000040" : "black"} />
                  <Text
                    color={allValuesAreDefault ? "#00000040" : "black"}
                    fontSize=".9rem"
                  >
                    Redefinir para valores padrão
                  </Text>
                </Flex>
              </Button>
            )}
            {tabStatus === "TRANSACTIONAL_COSTS" && editionModeState && (
              <Button
                disabled={allValuesAreEqualToCurrentSellerRates}
                bgColor="transparent"
                border="1px solid #D9D9D9"
                onClick={() => setUndoChangesAlerState(!undoChangesAlertState)}
              >
                <Text
                  color={
                    allValuesAreEqualToCurrentSellerRates
                      ? "#00000040"
                      : "black"
                  }
                  fontSize=".9rem"
                >
                  Descartar mudanças
                </Text>
              </Button>
            )}
            {tabStatus === "TRANSACTIONAL_COSTS" && (
              <Button
                bgColor={editionModeState ? undefined : "transparent"}
                border={editionModeState ? undefined : "1px solid #D9D9D9"}
                color={editionModeState ? undefined : "black"}
                onClick={handleConcludeSettings}
              >
                {editionModeState ? "Concluir" : "Editar"}
              </Button>
            )}
            {(tabStatus === "GENERAL" ||
              tabStatus === "INVOICE" ||
              tabStatus === "FINANCIAL") &&
              !isPageNotLoaded && (
                <Flex>
                  <Button
                    disabled={seller?.isBlocked}
                    onClick={() =>
                      navigate(`/seller/${id}/importar-assinaturas`)
                    }
                  >
                    Importar assinaturas
                  </Button>
                </Flex>
              )}
          </Flex>
        </Flex>
        <StickyContainer>
          <Tabs
            activeKey={tabStatus}
            className="tabs-custom"
            onChange={setTabStatus}
            renderTabBar={renderTabBar}
          >
            {filteredTabs.map(({ tab, key }) => (
              <TabPane tab={tab} key={key} disabled={editionModeState}>
                {tabStatus === "GENERAL" && (
                  <>
                    {(seller.invoice || seller.account) && (
                      <SellerForm seller={seller} />
                    )}
                  </>
                )}
                {tabStatus === "FINANCIAL" && (
                  <>
                    {(seller.invoice || seller.account) && (
                      <FinancialSection
                        account={seller?.account}
                        preApprovedValue={seller?.preApprovedValue}
                        hasAnticipation={seller?.activeAnticipation}
                        businessModelType="SPLIT_SUB_ACQUIRER"
                      />
                    )}
                  </>
                )}
                {tabStatus === "INVOICE" && (
                  <>
                    {(seller.invoice || seller.account) && (
                      <InvoiceSection invoice={seller?.invoice} />
                    )}
                  </>
                )}
                {tabStatus === "TRANSACTIONAL_COSTS" && (
                  <TransactionalCosts
                    defaultSellerRates={defaultSellerRates}
                    currentSellerRates={currentSellerRates}
                    colorFinder={colorFinder}
                    returnDefaultValue={returnDefaultValue}
                    hasReturnValue={hasReturnValue}
                    setCurrentSellerRates={setCurrentSellerRates}
                    onSubmit={onSubmit}
                    handleSubmit={handleSubmit}
                    register={register}
                    handleValue={handleValue}
                    watch={watch}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    setValuesToDefault={setValuesToDefault}
                    undoChanges={undoChanges}
                    confirmationModalState={confirmationModalState}
                    setConfirmationModalState={setConfirmationModalState}
                    editionModeState={editionModeState}
                    setEditionModeState={setEditionModeState}
                    allValuesAreDefault={allValuesAreDefault}
                    sellerRatesHistory={sellerRatesHistory}
                    handleGetSellerRatesHistory={handleGetSellerRatesHistory}
                    allValuesAreEqualToCurrentSellerRates={
                      allValuesAreEqualToCurrentSellerRates
                    }
                    undoChangesAlertState={undoChangesAlertState}
                    setUndoChangesAlerState={setUndoChangesAlerState}
                    defaultAlertState={defaultAlertState}
                    setDefaultAlertState={setDefaultAlertState}
                    allSellerRatesAreDefault={allSellerRatesAreDefault}
                    allBuyerRatesAreDefault={allBuyerRatesAreDefault}
                    handleGetFullSellerRatesHistory={
                      handleGetFullSellerRatesHistory
                    }
                    fullHistoryResponse={fullHistoryResponse}
                    setTabStatus={setTabStatus}
                    sellerSplitData={sellerSplitData}
                  />
                )}
                {tabStatus === "TRANSACTIONAL_COSTS v2" && (
                  <TransactionalCostsV2 />
                )}

                {tabStatus === "ANTECIPATION_ON_DEMAND" && (
                  <AntecipationOnDemand />
                )}
                {tabStatus === "ANTI_FRAUD" && (
                  <AntiFraudSection
                    defaultGhostModeEnable={seller?.ghostModeEnabled}
                    activeAntiFraud={seller?.activeAntiFraud}
                    antifraudService={seller?.antifraudService}
                  />
                )}
                {tabStatus === "BANKING" && <BankingSection seller={seller} />}
                {tabStatus === "PAYMENT_APPROVAL" && (
                  <PaymentApprovalSection />
                )}
                {tabStatus === "OWNERS" && <OwnersSection id={id} />}
              </TabPane>
            ))}
          </Tabs>
        </StickyContainer>
      </Container>
    </>
  );
};

export default WithAuth(Seller);
